import {
  Box,
  ButtonGroup,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  Zoom,
} from "@material-ui/core";
import { ThumbDownAlt, ThumbUpAlt, Update, Block } from "@material-ui/icons";
import * as React from "react";
import { FC, Fragment, useState } from "react";
import {
  Button,
  Datagrid,
  ExportButton,
  FormDataConsumer,
  FunctionField,
  NumberInput,
  SelectInput,
  TextField,
  TextInput,
  TopToolbar,
  required,
  useDataProvider,
  useNotify,
  useRefresh,
} from "react-admin";
import { DetailButton } from "../../../Components/DetailButton";
import { DefaultCreatedDateSort, DefaultPerPageSize } from "../../../Constants/Constant";
import { useAppDataProvider } from "../../../dataprovider";
import { TabbedDatagridProps } from "../../../Models/TabbedDataGridProps";
import { Form } from "react-final-form";
import { EnumField } from "../../../../src/Components/EnumField";
import { ExcelExport } from "../../../../src/Helper/ExportExcel";
import { SourceReliabilityRogues } from "./Constants";
import { ReliabilityRogueChoices, ReliabilityRogueStatus } from "../../../../src/Enums/ReliabilityRogueStatus";
import { ReliabilityRogueUpdateModel } from "../Models/ReliabilityRogueModel";
import {
  ReliabilityRogueConfirmedReasonType,
  ReliabilityRogueReasonChoices,
  ReliabilityRogueReasonType,
  ReliabilityRogueRejectReasonType,
} from "../../../../src/Enums/ReliabilityRogueReasonType";
import PoolList from "../../../Components/PoolListComponent/PoolListComponent";
import { CustomDatetime } from "../../../../src/Components/CustomDatetime";
import { CustomDateField } from "../../../../src/Components/CustomDateField";
import { formatDate } from "../../../../src/Helper/MomentHelper";
import { useAuth } from "@airborne/react-admin";

const TabbedDatagrid: FC<TabbedDatagridProps> = props => {
  const dataProvider = useAppDataProvider();
  const notify = useNotify();
  const refresh = useRefresh(); // Add this hook

  // Common method to update reliability rogue status
  const updateReliabilityRogue = (
    record: any,
    updates: Partial<ReliabilityRogueUpdateModel>,
    successMessage: string
  ) => {
    const reliabilityRogue: ReliabilityRogueUpdateModel = new ReliabilityRogueUpdateModel();
    reliabilityRogue.Id = record?.id;
    reliabilityRogue.GoodsRcvdBatch = record?.goodsRcvdBatch;
    reliabilityRogue.ReportId = record?.reportId;
    reliabilityRogue.AfterRemovalOrderType = record?.afterRemovalOrderType;
    reliabilityRogue.AfterRemovalOrderNumber = record?.afterRemovalOrderNumber;
    reliabilityRogue.AfterRemovalOrderStatus = record?.afterRemovalOrderStatus;
    reliabilityRogue.AfterRemovalVendorType = record?.afterRemovalVendorType;
    reliabilityRogue.AfterRemovalVendor = record?.afterRemovalVendor;
    reliabilityRogue.AfterRemovalCondition = record?.afterRemovalCondition;
    reliabilityRogue.AfterRemovalDate = record?.afterRemovalDate;
    reliabilityRogue.IsThreeConsecutive = record?.isThreeConsecutive;
    reliabilityRogue.IsFiveConsecutiveAvg = record?.isFiveConsecutiveAvg;
    reliabilityRogue.AverageFlightHourForFiveConsecutive = record?.averageFlightHourForFiveConsecutive;

    // Apply specific updates
    Object.assign(reliabilityRogue, updates);

    return dataProvider
      .putReliabilityRogue(reliabilityRogue)
      .then(result => {
        notify(successMessage, "success", false, true);
        refresh();
      })
      .catch(result => notify(`${result}`, "error", false, true));
  };

  const AcceptRogueButton = props => {
    const [open, setOpen] = useState(false);

    const handleClick = e => {
      e.stopPropagation();
      setOpen(true);
    };
    const handleClose = () => {
      setOpen(false);
    };

    const ClosePopup = () => {
      setOpen(false);
    };

    const acceptButtonClick = record => e => {
      updateReliabilityRogue(
        props?.record,
        {
          Reason: e.reason,
          OtherReason: e.otherReason,
          RogueStatus: ReliabilityRogueStatus.Confirmed,
        },
        "Reliability Rogue Updated"
      );
    };

    return (
      <>
        <IconButton {...props} style={{ width: 40, color: "#12824C" }} onClick={handleClick}>
          <Tooltip TransitionComponent={Zoom} title="Accept Rogue Candidate">
            <ThumbUpAlt />
          </Tooltip>
        </IconButton>

        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>Accept Rogue Candidate</DialogTitle>
          <DialogContent>
            <Form
              onSubmit={acceptButtonClick(props)}
              render={({ handleSubmit }) => (
                <form>
                  <Grid container>
                    <Grid item xs={12}>
                      <SelectInput
                        source="reason"
                        label="Confirm Action"
                        choices={ReliabilityRogueConfirmedReasonType}
                        optionText="key"
                        optionValue="value"
                        validate={required()}
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button label={"Accept Candidate"} size="large" variant="contained" onClick={handleSubmit}>
                        <Update />
                      </Button>
                      <Button label={"Cancel!"} size="large" variant="contained" onClick={ClosePopup} color="secondary">
                        <Update />
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              )}
            />
          </DialogContent>
        </Dialog>
      </>
    );
  };

  const RejectRogueButton = props => {
    const [open, setOpen] = useState(false);

    const handleClick = e => {
      e.stopPropagation();
      setOpen(true);
    };
    const handleClose = () => {
      setOpen(false);
    };

    const RejectReliabilityRogueButtonClick = props => e => {
      updateReliabilityRogue(
        props?.record,
        {
          Reason: e.reason,
          OtherReason: e.otherReason,
          RogueStatus: ReliabilityRogueStatus.Rejected,
        },
        "Reliability Rogue Updated"
      ).then(() => setOpen(false));
    };

    const ClosePopup = () => {
      setOpen(false);
    };

    return (
      <>
        <IconButton {...props} style={{ width: 40 }} onClick={handleClick}>
          <Tooltip TransitionComponent={Zoom} title="Reject Rogue Candidate">
            <ThumbDownAlt />
          </Tooltip>
        </IconButton>

        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>Reject Rogue Candidate</DialogTitle>
          <DialogContent>
            <Form
              onSubmit={RejectReliabilityRogueButtonClick(props)}
              render={({ handleSubmit }) => (
                <form>
                  <Grid container>
                    <Grid item xs={12}>
                      <SelectInput
                        source="reason"
                        label="Reject Reason"
                        choices={ReliabilityRogueRejectReasonType}
                        optionText="key"
                        optionValue="value"
                        validate={required()}
                        fullWidth
                        variant="outlined"
                      />
                      <FormDataConsumer>
                        {({ formData, ...rest }) =>
                          formData.reason === ReliabilityRogueReasonType.Other && (
                            <TextInput
                              source="otherReason"
                              label="Other Reason"
                              fullWidth
                              variant="outlined"
                              multiline
                              {...rest}
                            />
                          )
                        }
                      </FormDataConsumer>
                    </Grid>
                    <Grid item xs={12}>
                      <Button label={"Reject Rogue Candidate"} size="large" variant="contained" onClick={handleSubmit}>
                        <Update />
                      </Button>
                      <Button label={"Cancel!"} size="large" variant="contained" onClick={ClosePopup} color="secondary">
                        <Update />
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              )}
            />
          </DialogContent>
        </Dialog>
      </>
    );
  };

  const MarkAsNotRogueButton = props => {
    const [open, setOpen] = useState(false);

    const handleClick = e => {
      e.stopPropagation();
      setOpen(true);
    };

    const handleClose = () => {
      setOpen(false);
    };

    const ClosePopup = () => {
      setOpen(false);
    };

    const markAsNotRogueButtonClick = e => {
      updateReliabilityRogue(
        props?.record,
        {
          OtherReason: e.otherReason,
          RogueStatus: ReliabilityRogueStatus.NotRogue,
          Reason: ReliabilityRogueReasonType.Other,
        },
        "Marked as Not Rogue"
      ).then(() => setOpen(false));
    };

    return (
      <>
        <IconButton {...props} style={{ width: 40 }} onClick={handleClick} color="primary">
          <Tooltip TransitionComponent={Zoom} title="Mark as Not Rogue">
            <Block />
          </Tooltip>
        </IconButton>

        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>Mark as Not Rogue</DialogTitle>
          <DialogContent>
            <Form
              onSubmit={markAsNotRogueButtonClick}
              render={({ handleSubmit }) => (
                <form>
                  <Grid container>
                    <Grid item xs={12}>
                      <TextInput
                        source="otherReason"
                        label="Other Reason"
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={4}
                        validate={required()}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        label={"Not Rogue"}
                        size="large"
                        variant="contained"
                        onClick={handleSubmit}
                        color="primary"
                        style={{ marginRight: 8 }}
                      >
                        <Update />
                      </Button>
                      <Button label={"Cancel"} size="large" variant="contained" onClick={ClosePopup} color="secondary">
                        <Update />
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              )}
            />
          </DialogContent>
        </Dialog>
      </>
    );
  };

  const DetailsButton = ({ record }) => {
    return <DetailButton record={record} resource={SourceReliabilityRogues} />;
  };

  const OptButtonGroup = ({ record }) => {
    return (
      <Box style={{ flex: 1, flexDirection: "row" }}>
        <ButtonGroup variant="text" size="small">
          <DetailsButton record={record} />
          <>
            <AcceptRogueButton record={record} />
            <RejectRogueButton record={record} />
            <MarkAsNotRogueButton record={record} />
          </>
        </ButtonGroup>
      </Box>
    );
  };

  return (
    <Fragment>
      <Datagrid optimized basePath={SourceReliabilityRogues}>
        <OptButtonGroup record={props} />
        <TextField source="goodsRcvdBatch" label="Goods Received Batch" />
        <TextField source="pn" label="Pn" />
        <TextField source="sn" label="Sn" />
        <TextField source="pnDescription" label="Pn Description" />
        <FunctionField
          label="Rogue Date"
          sortBy="createdDate"
          render={(record: any) => {
            return record.createdDate === null ? null : formatDate(record.createdDate);
          }}
        />
        <CustomDateField source="lastTransactionDate" label={"Last Transaction Date"} locales="en-GB" />
        <TextField source="lastTransactionType" label="Last Transaction Type" />
        <EnumField label={"Rogue Status"} enumName={"ReliabilityRogueStatus"} source={"rogueStatus"} />
        <EnumField label={"Action/Reason"} enumName={"ReliabilityRogueReasonType"} source={"reason"} />
        <FunctionField
          label="Target Date"
          sortBy="targetDate"
          render={(record: any) => {
            return record.targetDate === null ? null : formatDate(record.targetDate);
          }}
        />
      </Datagrid>
    </Fragment>
  );
};

const RogueList = props => {
  const { user } = useAuth();
  const dataProvider = useDataProvider();
  const [customers, setCustomers] = useState<any>();

  React.useEffect(() => {
    getCustomerData();
  }, []);

  const getCustomerData = async () => {
    await dataProvider
      .getList("cdmheaders", {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "customerCode", order: "ASC" },
        filter: {},
      })
      .then(response => {
        const customerCodes = [...new Set(response.data?.map(x => x.ownerName))];
        const customerData = customerCodes?.map(ownerName => ({ id: ownerName, customerCode: ownerName }));
        setCustomers(customerData);
      });
  };

  const ListActions = props => {
    return (
      <TopToolbar {...props}>
        <ExportButton
          resource={props.resource}
          sort={props.currentSort}
          filterValues={props.filterValues}
          maxResults={10000}
        />
      </TopToolbar>
    );
  };

  const pickLatest = (last: Remark | null, curr): boolean => {
    return !last || new Date(curr.createdDate) > new Date(last.createdDate);
  };

  const exporter = records => {
    const data = records.map(record => {
      const remarks = record?.reliabilityReportRemark ?? [];
      let latestInvisible: Remark | null = null;
      let latestVisible: Remark | null = null;

      for (const r of (remarks as Remark[]) ?? []) {
        if (user?.isCustomer) {
          if (r.isVisibleToCustomer && pickLatest(latestVisible, r)) {
            latestVisible = r;
          }
          continue;
        }
        if (r.isVisibleToCustomer === true) {
          if (pickLatest(latestVisible, r)) {
            latestVisible = r;
          }
        } else if (r.isVisibleToCustomer === false) {
          if (pickLatest(latestInvisible, r)) {
            latestInvisible = r;
          }
        }
      }

      const mappedRecord = {
        "Goods Received Batch": record.goodsRcvdBatch,
        Pn: record.pn,
        Sn: record.sn,
        Chapter: record.chapter,
        "Pn Description": record.pnDescription,
        "Rogue Candidate Status": ReliabilityRogueChoices.find(x => x.value === record.rogueStatus)?.key,
        "Action/Reason": ReliabilityRogueReasonChoices.find(x => x.value === record.reason)?.key,
        "Other Reason": record.otherReason,
        "After Removal Order Type": record.afterRemovalOrderType,
        "After Removal Order Number": record.afterRemovalOrderNumber,
        "After Removal Order Status": record.afterRemovalOrderStatus,
        "After Removal Vendor Type": record.afterRemovalVendorType,
        "After Removal Vendor": record.afterRemovalVendor,
        "After Removal Condition": record.afterRemovalCondition,
        "After Removal Date": record.afterRemovalDate != null ? record.afterRemovalDate : "",
        "Last Transaction Type": record.lastTransactionType,
        "Last Transaction Date": record.lastTransactionDate ?? "",
        "Three Consecutive Fails": record.isThreeConsecutive,
        "Five Consecutive Average FH": record.isFiveConsecutiveAvg,
        "Average Flight Five Consecutive": record.averageFlightHourForFiveConsecutive,
        "Target Date": record.targetDate,
        "Rogue Date": record.createdDate,
        "Visible Remark": latestVisible?.remark ?? "",
        "Visible Remark By": latestVisible?.createdBy ?? "",
        "Visible Remark Date": latestVisible?.createdDate ?? "",
        "Invisible Remark": latestInvisible?.remark ?? "",
        "Invisible Remark By": latestInvisible?.createdBy ?? "",
        "Invisible Remark Date": latestInvisible?.createdDate ?? "",
      };
      return mappedRecord;
    });

    const columns = Object.keys(data[0]).map(record => ({ header: record }));
    ExcelExport(columns, data, "Rogue Candidate List");
  };

  const rogueStatusChoices = [{ key: "ALL" }, ...ReliabilityRogueChoices];

  const filters = [
    <SelectInput
      alwaysOn
      optionText="customerCode"
      optionValue="id"
      label="Customer"
      source="customer__eq"
      choices={customers}
    />,
    <NumberInput label="Goods Rcvd Batch" source="goodsRcvdBatch__eq" alwaysOn />,
    <TextInput label="Pn" source="pn__contains" alwaysOn />,
    <TextInput label="Sn" source="sn__contains" alwaysOn />,
    <TextInput label="Pn Description" source="pnDescription__contains" alwaysOn />,
    <CustomDatetime source="lastTransactionDate__gte" label="Last Transaction Date(>=)" alwaysOn />,
    <CustomDatetime source="lastTransactionDate__lte" label="Last Transaction Date (<=)" alwaysOn />,
    <CustomDatetime source="installDate__gte" label="Install Date (>=)" alwaysOn />,
    <CustomDatetime source="removeDate__lte" label="Remove Date (<=)" alwaysOn />,
    <CustomDatetime source="targetDate__gte" label="Target Date (>=)" alwaysOn />,
    <CustomDatetime source="targetDate__lte" label="Target Date (<=)" alwaysOn />,
    <SelectInput
      label="Rogue Status"
      source="rogueStatus__eq"
      choices={rogueStatusChoices}
      optionValue="value"
      optionText="key"
      alwaysOn
    />,
  ];

  return (
    <Grid container>
      <Grid item xs={12}>
        <PoolList
          {...props}
          hasCreate={false}
          exporter={exporter}
          actions={<ListActions />}
          bulkActionButtons={false}
          resource={SourceReliabilityRogues}
          title={"Rogue Candidate"}
          sort={DefaultCreatedDateSort}
          perPage={DefaultPerPageSize}
          filters={filters}
          filter={props?.filterParams}
        >
          <TabbedDatagrid />
        </PoolList>
      </Grid>
    </Grid>
  );
};

export default RogueList;
